import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PlateCells } from "@/components/designer/RoomPreview";
import { api } from "@/lib/api";
import { useCart, useCartActions } from "@/lib/cart";
import { LAYOUT_LABEL } from "@/lib/designer";
import { formatCOP, useSeo } from "@/lib/site";

const qBtn = "grid h-9 w-9 place-items-center rounded-full border border-[#080808]/15 transition-colors hover:border-[#080808] disabled:opacity-30";

const Thumb = ({ item, templates, photos }) => {
  const box = 112;
  const s = box / Math.max(item.width_cm, item.height_cm);
  const w = item.width_cm * s, h = item.height_cm * s;
  return (
    <div className="grid h-[128px] w-[128px] shrink-0 place-items-center bg-[#EAE7E2]">
      <div className="relative overflow-hidden shadow-[4px_6px_12px_-4px_rgba(8,8,8,0.35)]" style={{ width: w, height: h, borderRadius: Math.max(2, w * 0.018) }}>
        <PlateCells w={w} h={h} rects={templates[item.layout][item.orientation]} cells={item.cells} photos={photos} orientation={item.orientation} />
      </div>
    </div>
  );
};

const CartItem = ({ item, templates, photos }) => {
  const { qty, remove } = useCartActions();
  const run = (p) => p.catch((e) => toast.error(e.message));
  return (
    <li data-testid={`cart-item-${item.id}`} className="flex flex-col gap-5 border-b border-[#EAE7E2] py-6 sm:flex-row">
      <Thumb item={item} templates={templates} photos={photos} />
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-serif-display text-2xl font-medium">Lámina LUMINA {item.size}</h2>
          <p data-testid={`cart-item-${item.id}-line-total`} className="text-lg font-medium">{formatCOP(item.line_total_cop)}</p>
        </div>
        <p data-testid={`cart-item-${item.id}-details`} className="text-sm text-[#080808]/70">
          {item.width_cm} × {item.height_cm} cm · {item.orientation === "vertical" ? "Vertical" : "Horizontal"} · {LAYOUT_LABEL[item.layout]} · {formatCOP(item.unit_price_cop)} c/u
        </p>
        {item.has_pending_changes && (
          <p data-testid={`cart-item-${item.id}-pending`} className="text-xs text-[#7a5a2e]">Este diseño tiene cambios sin confirmar. El carrito conserva la última versión confirmada.</p>
        )}
        {!item.available && <p className="text-xs text-[#8B1E1E]">Este tamaño ya no está disponible.</p>}
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2" aria-label="Cantidad">
            <button type="button" aria-label="Disminuir cantidad" data-testid={`cart-item-${item.id}-dec`} className={qBtn} disabled={item.quantity <= 1 || qty.isPending}
              onClick={() => run(qty.mutateAsync({ id: item.id, quantity: item.quantity - 1 }))}><Minus className="h-4 w-4" /></button>
            <span data-testid={`cart-item-${item.id}-qty`} className="w-8 text-center text-base font-medium">{item.quantity}</span>
            <button type="button" aria-label="Aumentar cantidad" data-testid={`cart-item-${item.id}-inc`} className={qBtn} disabled={item.quantity >= 20 || qty.isPending}
              onClick={() => run(qty.mutateAsync({ id: item.id, quantity: item.quantity + 1 }))}><Plus className="h-4 w-4" /></button>
          </div>
          <Link to={`/personalizar?diseno=${item.design_id}`} data-testid={`cart-item-${item.id}-edit`}
            className="text-xs font-semibold uppercase tracking-[0.12em] underline underline-offset-4 hover:text-[#B18A4B]">Editar diseño</Link>
          <button type="button" data-testid={`cart-item-${item.id}-remove`} onClick={() => run(remove.mutateAsync(item.id).then(() => toast.success("Producto eliminado")))}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#8B1E1E] hover:underline"><Trash2 className="h-3.5 w-3.5" /> Eliminar</button>
        </div>
      </div>
    </li>
  );
};

export default function CartPage() {
  useSeo({ title: "Carrito | LUMINA STUDIO", noindex: true });
  const { data: cart, isLoading } = useCart();
  const { data: cfg } = useQuery({ queryKey: ["designer-config"], queryFn: () => api.get("/designer/config").then((r) => r.data) });
  const { data: photoList = [] } = useQuery({ queryKey: ["designer-photos"], queryFn: () => api.get("/designer/photos").then((r) => r.data), enabled: !!cart?.items.length });
  const photos = useMemo(() => Object.fromEntries(photoList.map((p) => [p.id, p])), [photoList]);
  if (isLoading || !cfg) return <div className="min-h-[60vh]" />;
  const cta = "inline-flex justify-center rounded-full bg-[#080808] px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B]";

  if (!cart.items.length) return (
    <div data-testid="cart-page" className="mx-auto max-w-2xl px-5 sm:px-8 py-20 sm:py-28 text-center">
      <h1 className="font-serif-display text-[34px] sm:text-[48px] font-medium leading-tight">Tu carrito</h1>
      <p data-testid="cart-empty" className="mt-6 text-base text-[#080808]/75">Tu carrito está vacío. Crea tu primera lámina con tus fotografías.</p>
      <Link to="/personalizar" data-testid="cart-empty-customize" className={`mt-10 ${cta}`}>Personaliza tu LUMINA</Link>
    </div>
  );

  return (
    <div data-testid="cart-page" className="mx-auto grid max-w-[1300px] gap-12 px-5 sm:px-8 lg:px-12 py-12 sm:py-20 lg:grid-cols-[1fr_360px]">
      <div>
        <h1 className="font-serif-display text-[34px] sm:text-[48px] font-medium leading-tight">Tu carrito</h1>
        <ul className="mt-6 border-t border-[#EAE7E2]">
          {cart.items.map((i) => <CartItem key={i.id} item={i} templates={cfg.templates} photos={photos} />)}
        </ul>
        <Link to="/personalizar" data-testid="cart-add-another" className="mt-6 inline-block text-sm font-semibold uppercase tracking-[0.12em] underline underline-offset-4 hover:text-[#B18A4B]">+ Crear otra lámina</Link>
      </div>
      <aside data-testid="cart-summary" className="self-start bg-white p-6 sm:p-8 lg:sticky lg:top-28">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em]">Resumen</h2>
        <div className="mt-5 flex justify-between text-sm"><span>Láminas</span><span data-testid="cart-count">{cart.count}</span></div>
        <div className="mt-3 flex justify-between text-sm"><span>Envío</span><span className="text-[#080808]/60">Se calcula al finalizar</span></div>
        <div className="mt-5 flex items-baseline justify-between border-t border-[#EAE7E2] pt-5">
          <span className="text-sm font-semibold uppercase tracking-[0.12em]">Subtotal</span>
          <span data-testid="cart-subtotal" className="font-serif-display text-3xl font-medium">{formatCOP(cart.subtotal_cop)}</span>
        </div>
        <Link to="/checkout" data-testid="checkout-button" className="mt-6 block w-full rounded-full bg-[#080808] py-4 text-center text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B]">
          Continuar al pago
        </Link>
        <p data-testid="checkout-note" className="mt-3 text-xs leading-relaxed text-[#080808]/60">Pago seguro con Wompi. Envío contra entrega.</p>
      </aside>
    </div>
  );
}
